import { useEffect, useRef } from "react";
import { motion } from "framer-motion";

export const Reveal = ({ children, delay = 0, y = 36, className = "", ...rest }) => (
  <motion.div
    initial={{ opacity: 0, y }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-60px" }}
    transition={{ duration: 0.8, delay, ease: [0.22, 1, 0.36, 1] }}
    className={className}
    {...rest}
  >
    {children}
  </motion.div>
);

export const SectionHeading = ({ eyebrow, title, titleAccent, sub, align = "center" }) => (
  <div className={`mb-12 sm:mb-20 ${align === "center" ? "text-center" : ""}`}>
    <Reveal>
      <span className="inline-block text-[11px] sm:text-xs font-bold tracking-[0.3em] uppercase text-[#D4AF37] mb-4">
        {eyebrow}
      </span>
    </Reveal>
    <Reveal delay={0.1}>
      <h2 className="font-display text-3xl sm:text-5xl font-bold text-white leading-tight">
        {title} {titleAccent && <span className="text-[#D4AF37]">{titleAccent}</span>}
      </h2>
    </Reveal>
    {sub && (
      <Reveal delay={0.2}>
        <p className={`text-white/45 text-sm sm:text-base mt-4 max-w-xl ${align === "center" ? "mx-auto" : ""}`}>{sub}</p>
      </Reveal>
    )}
  </div>
);

export const LightBeams = ({ variant = "gold" }) => (
  <>
    <div className={`home-beam ${variant === "red" ? "home-beam--red" : ""}`} style={{ top: "10%" }} />
    <div className="home-beam home-beam--red" style={{ top: "60%", animationDelay: "-8s" }} />
  </>
);

export const Particles = ({ density = 34, className = "" }) => {
  const ref = useRef(null);

  useEffect(() => {
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
    const canvas = ref.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    let raf, w, h;
    let parts = [];

    const resize = () => {
      const r = canvas.parentElement.getBoundingClientRect();
      w = canvas.width = r.width;
      h = canvas.height = r.height;
    };
    resize();

    parts = Array.from({ length: density }, () => ({
      x: Math.random(), y: Math.random(),
      r: 0.5 + Math.random() * 1.4,
      s: 0.02 + Math.random() * 0.06,
      a: Math.random() * Math.PI * 2,
      gold: Math.random() > 0.5,
    }));

    const tick = () => {
      ctx.clearRect(0, 0, w, h);
      for (const p of parts) {
        p.y -= p.s / 100;
        p.a += 0.015;
        if (p.y < -0.02) { p.y = 1.02; p.x = Math.random(); }
        const alpha = 0.12 + Math.sin(p.a) * 0.1;
        ctx.beginPath();
        ctx.arc(p.x * w, p.y * h, p.r, 0, Math.PI * 2);
        ctx.fillStyle = p.gold ? `rgba(212,175,55,${alpha})` : `rgba(255,255,255,${alpha * 0.7})`;
        ctx.fill();
      }
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    window.addEventListener("resize", resize);
    return () => { cancelAnimationFrame(raf); window.removeEventListener("resize", resize); };
  }, [density]);

  return <canvas ref={ref} className={`absolute inset-0 w-full h-full pointer-events-none ${className}`} aria-hidden="true" />;
};
